import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserRole, ROLES } from '../constants/roles';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole | UserRole[];
}

/**
 * ProtectedRoute component that checks:
 * 1. User is authenticated (token exists)
 * 2. If requiredRole is specified, user has the correct role
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole 
}) => {
  const { user, token } = useAuth();

  // Check if user is authenticated
  if (!token || !user) {
    console.log('ProtectedRoute: User not authenticated, redirecting to login');
    return <Navigate to="/" replace />;
  }

  // If specific role(s) required, check if user has access
  if (requiredRole) {
    const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    
    // Normalize user role for comparison
    const normalizedUserRole = (user.role.toUpperCase().replace('-', '_')) as UserRole;
    
    if (!allowedRoles.includes(normalizedUserRole)) {
      console.log(`ProtectedRoute: User role ${normalizedUserRole} not in allowed roles`, allowedRoles);
      // Redirect to unauthorized or home page
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
